'use client'

import { useState, useEffect, useCallback } from 'react'
import { ConnectorManager } from '../../ConnectorManager'
import { Position } from '../../types'

/**
 * Custom hook for managing Groww positions data
 */
export function useGrowwPositions() {
  const [positions, setPositions] = useState<Position[]>([])
  const [rawPositionData, setRawPositionData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPositions = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      // Get the Groww connector
      const connectorManager = ConnectorManager.getInstance()
      const growwConnector = connectorManager.getConnector('groww')
      
      if (!growwConnector) {
        throw new Error('Groww connector not found or not connected')
      }
      
      // Use the getPositions method from the connector
      const positionsData = await growwConnector.getPositions()
      
      if (Array.isArray(positionsData) && positionsData.length > 0) {
        // Store raw data for details popup
        setRawPositionData(positionsData)
        
        // Map API data to Position interface
        const mappedPositions = positionsData.map((position: any) => ({
          symbol: position.symbol || position.trading_symbol,
          quantity: position.quantity || 0,
          averagePrice: position.averagePrice || position.net_price || 0,
          currentPrice: position.currentPrice || position.lastPrice || 0,
          pnl: position.pnl || 0 // Use the pre-calculated P&L from the API
        }))
        
        setPositions(mappedPositions)
      } else {
        console.log('No positions data returned or empty array')
        setPositions([])
        setRawPositionData([])
      }
    } catch (error: any) {
      console.error('Error fetching positions:', error)
      setError(error.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // Fetch positions on mount
    fetchPositions()
  }, [])

  return {
    positions,
    rawPositionData,
    isLoading,
    error,
    fetchPositions
  }
} 